import { useState } from 'react';
import { Play, RefreshCw, CheckCircle, XCircle, AlertCircle, FileText, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'running':
      return <RefreshCw className="w-5 h-5 text-primary animate-spin" />;
    case 'failed':
      return <XCircle className="w-5 h-5 text-red-600" />;
    default:
      return <RefreshCw className="w-5 h-5 text-gray-400" />;
  }
};

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'text-red-600 bg-red-50 border-red-200';
    case 'high':     return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'medium':   return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    default:         return 'text-primary bg-blue-50 border-blue-200';
  }
};

const PlanBuilder = ({
  userGoal,
  context,
  loading,
  plan,
  executing,
  executionResult,
  onGoalChange,
  onContextChange,
  onCreatePlan,
  onApprovePlan,
  onExecutePlan,
}) => {
  return (
    <div className="space-y-8">
      {/* Goal Input Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Define Your Goal</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What would you like to accomplish?
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={4}
              placeholder="E.g., 'Connect to our PostgreSQL database, analyze customer data for PII, generate descriptions for all tables, and ensure we're compliant with GDPR'"
              value={userGoal}
              onChange={(e) => onGoalChange(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Context (Optional JSON)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows={3}
              placeholder='{"data_source": "postgres://...", "requirements": ["GDPR", "CCPA"]}'
              value={context}
              onChange={(e) => onContextChange(e.target.value)}
            />
          </div>
          <button
            onClick={onCreatePlan}
            disabled={loading || !userGoal.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Analyzing & Planning... (may take up to 2 min)
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Create Orchestration Plan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Plan Display */}
      {plan && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-semibold">Orchestration Plan</h2>
                  {getStatusIcon(plan.status)}
                  <span className="text-sm text-gray-600">Status: {plan.status}</span>
                </div>
                <p className="text-sm text-gray-500">Plan ID: {plan.id}</p>
              </div>
              <div className="text-right text-sm text-gray-600">
                <div>Estimated Cost: ${plan.estimated_cost?.toFixed(2) || '0.00'}</div>
                <div>
                  Estimated Duration:{' '}
                  {plan.estimated_duration_ms
                    ? `${(plan.estimated_duration_ms / 1000).toFixed(1)}s`
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Intent Analysis */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2 text-gray-800">Intent Analysis</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Primary Task:</span>
                  <p className="text-gray-600 mt-0.5">{plan.intent_analysis?.primary_task}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Complexity:</span>
                  <p className="text-gray-600 capitalize mt-0.5">{plan.intent_analysis?.complexity}</p>
                </div>
              </div>
              {plan.intent_analysis?.required_capabilities?.length > 0 && (
                <div className="mt-2">
                  <span className="font-medium text-sm text-gray-700">Required Capabilities:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {plan.intent_analysis.required_capabilities.map((cap, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-primaryDark rounded text-xs">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Governance Flags */}
            {plan.governance_flags && plan.governance_flags.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-gray-800">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  Governance Flags
                </h3>
                <div className="space-y-2">
                  {plan.governance_flags.map((flag, idx) => (
                    <div key={idx} className={`p-3 border rounded-lg ${getSeverityColor(flag.severity)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-sm">{flag.flag_type}</div>
                          <div className="text-sm mt-1">{flag.message}</div>
                        </div>
                        <span className="text-xs font-semibold uppercase">{flag.severity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approval Section */}
            {plan.requires_approval && !plan.user_approved && plan.status === 'awaiting_approval' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-yellow-800 mb-2">Approval Required</h3>
                <p className="text-sm text-yellow-700 mb-4">{plan.approval_reason}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onApprovePlan(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                  >
                    Approve & Continue
                  </button>
                  <button
                    onClick={() => onApprovePlan(false)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}

            {/* Execute button */}
            {plan.user_approved && plan.status === 'approved' && !executing && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Plan Approved - Ready for Execution
                </h3>
                <p className="text-sm text-green-700 mb-4">
                  The orchestration plan has been approved and is ready to execute.
                </p>
                <button
                  onClick={onExecutePlan}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-2 font-semibold text-sm"
                >
                  <Play className="w-5 h-5" />
                  Execute Orchestration Workflow
                </button>
              </div>
            )}

            {/* Status display for running/completed/failed */}
            {['running', 'completed', 'failed', 'partial_success'].includes(plan.status) && (
              <div className={`border rounded-lg p-4 mb-4 ${
                plan.status === 'completed'       ? 'bg-green-50 border-green-200' :
                plan.status === 'running'         ? 'bg-blue-50 border-blue-200' :
                plan.status === 'failed'          ? 'bg-red-50 border-red-200' :
                'bg-yellow-50 border-yellow-200'
              }`}>
                <h3 className={`font-semibold mb-2 flex items-center gap-2 ${
                  plan.status === 'completed'       ? 'text-green-800' :
                  plan.status === 'running'         ? 'text-primaryDark' :
                  plan.status === 'failed'          ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {getStatusIcon(plan.status)}
                  Workflow{' '}
                  {plan.status === 'running'  ? 'Executing' :
                   plan.status === 'completed' ? 'Completed' :
                   plan.status === 'failed'    ? 'Failed' : 'Partially Completed'}
                </h3>
                <p className={`text-sm ${
                  plan.status === 'completed' ? 'text-green-700' :
                  plan.status === 'running'   ? 'text-primaryDark' :
                  plan.status === 'failed'    ? 'text-red-700' : 'text-yellow-700'
                }`}>
                  {plan.status === 'running'
                    ? 'The orchestration workflow is currently executing.'
                    : plan.status === 'completed'
                    ? 'All agents have completed successfully.'
                    : plan.status === 'failed'
                    ? 'The workflow encountered errors.'
                    : 'Some agents completed, others failed.'}
                </p>
              </div>
            )}

            {executing && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-primary animate-spin" />
                <span className="text-primaryDark text-sm">Orchestration executing...</span>
              </div>
            )}
          </div>

          {/* Execution Results */}
          {executionResult && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Execution Results</h2>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-center">
                  <div className="text-3xl font-bold text-green-600">{executionResult.completed_agents}</div>
                  <div className="text-sm text-gray-600 mt-1">Completed Agents</div>
                </div>
                <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-center">
                  <div className="text-3xl font-bold text-red-600">{executionResult.failed_agents}</div>
                  <div className="text-sm text-gray-600 mt-1">Failed Agents</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
                  <div className="text-3xl font-bold text-primaryDark">
                    {plan.actual_duration_ms ? `${(plan.actual_duration_ms / 1000).toFixed(1)}s` : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Actual Duration</div>
                </div>
              </div>

              {plan.executions && plan.executions.length > 0 && (() => {
                const uniqueExecs = plan.executions.filter((exec, idx, arr) =>
                  arr.findLastIndex((e) => e.agent_id === exec.agent_id) === idx
                );
                const descriptionAgent = uniqueExecs.find((exec) =>
                  exec.agent_id.includes('description-agent') || exec.agent_id.includes('description_agent')
                );
                const otherAgents = uniqueExecs.filter((exec) =>
                  !exec.agent_id.includes('description-agent') && !exec.agent_id.includes('description_agent')
                );

                return (
                  <>
                    {descriptionAgent?.status === 'completed' && descriptionAgent.outputs && (() => {
                      const outputs = typeof descriptionAgent.outputs === 'string'
                        ? JSON.parse(descriptionAgent.outputs).outputs || JSON.parse(descriptionAgent.outputs)
                        : descriptionAgent.outputs.outputs || descriptionAgent.outputs;
                      const content = outputs.answer || outputs.documentation || outputs.message;
                      if (!content) return null;
                      return (
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-5 h-5 text-primaryDark" />
                            <h3 className="text-lg font-semibold text-gray-900">Summary Report</h3>
                          </div>
                          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                            <div className="prose prose-sm max-w-none">
                              <ReactMarkdown>{content}</ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <details open>
                      <summary className="cursor-pointer list-none">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <h3 className="font-semibold text-gray-700">
                            Agent Execution Log ({otherAgents.length + (descriptionAgent ? 1 : 0)} agents)
                          </h3>
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        </div>
                      </summary>
                      <div className="mt-3 space-y-2">
                        {otherAgents.map((exec, idx) => (
                          <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-medium text-gray-900 text-sm">{exec.agent_id}</span>
                                <span className="text-sm text-gray-500 ml-2">({exec.capability})</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(exec.status)}
                                <span className="text-sm font-medium text-gray-700">{exec.status}</span>
                                {exec.duration_ms && (
                                  <span className="text-xs text-gray-400">({exec.duration_ms}ms)</span>
                                )}
                              </div>
                            </div>
                            {exec.status === 'completed' && exec.outputs && (
                              <details className="mt-2">
                                <summary className="text-xs font-semibold text-primaryDark cursor-pointer hover:text-primaryDark">
                                  View Raw Output
                                </summary>
                                <pre className="mt-2 p-3 bg-gray-50 rounded border text-xs font-mono text-gray-700 whitespace-pre-wrap break-words overflow-x-auto">
                                  {typeof exec.outputs === 'string'
                                    ? JSON.stringify(JSON.parse(exec.outputs).outputs || JSON.parse(exec.outputs), null, 2)
                                    : JSON.stringify(exec.outputs.outputs || exec.outputs, null, 2)}
                                </pre>
                              </details>
                            )}
                            {exec.error_message && (
                              <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                                <span className="font-semibold">Error:</span> {exec.error_message}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanBuilder;
